import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-alpine.css";
import { SquarePen, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
import ListView from "../../shared/ListView";
import ConfirmModal from "../../shared/ConfirmModal";
// import your actual API hooks
import {
  useGetGroupsQuery,
  useDeleteGroupMutation,
  // useBulkDeleteGroupsMutation
} from "../../../../services/redux/api/components";
import AddGroupMaster from "./AddGroupMaster";

const GroupMaster = () => {
  const navigate = useNavigate();
  const gridRef = useRef(null);

  const [deleteGroup, { isLoading: isDeletingSingle }] = useDeleteGroupMutation();
  // const [bulkDeleteGroup, { isLoading: isDeletingBulk }] = useBulkDeleteGroupsMutation();

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [deleteId, setDeleteId] = useState(null);
  const [deleteMode, setDeleteMode] = useState("single");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedNames, setSelectedNames] = useState([]);
  const selectedIdsRef = useRef([]);
  const [allSelected, setAllSelected] = useState(false);
  const [preserveSelection, setPreserveSelection] = useState(false);

  // Filter & Sort
  const [filterColumn, setFilterColumn] = useState("GroupName");
  const [filterValueInput, setFilterValueInput] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [columnOptions, setColumnOptions] = useState([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const {
    data: groupsData,
    isLoading,
    error,
    refetch,
  } = useGetGroupsQuery({
    pageNumber: currentPage,
    pageSize: itemsPerPage,
    sortField,
    sortOrder: sortDirection,
    ...(isFilterApplied && {
      searchTerm: filterColumn,
      searchValue: filterValue,
    }),
  });

  // Setup column filter options on mount
  useEffect(() => {
    const options = [
      { value: "GroupCode", label: "Group Code" },
      { value: "GroupName", label: "Group Name" },
    ];
    setColumnOptions(options);
  }, []);

  // Prepare rowData from API result
  const rowData = useMemo(() => {
    if (!groupsData?.data) return [];
    return groupsData.data.map(item => ({
      ...item,
      id: item.id ?? item.groupId ?? item.GroupCode, // adapt depending on response shape
    }));
  }, [groupsData, isFilterApplied]);

  const handleFilter = () => {
    if (!filterValueInput.trim()) {
      toast.warning("Please enter filter value");
      return;
    }
    setFilterValue(filterValueInput);
    setIsFilterApplied(true);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilterValueInput("");
    setFilterValue("");
    setIsFilterApplied(false);
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    let direction = "asc";
    if (sortField === field) {
      direction = sortDirection === "asc" ? "desc" : "asc";
    }
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const createHeaderComponent = (field, displayName) => {
    return () => {
      const isSorted = sortField === field;
      const isAsc = sortDirection === "asc";
      return (
        <div
          className="cursor-pointer hover:text-brandRed flex items-center"
          onClick={() => handleSort(field)}
        >
          {displayName}
          {isSorted && (
            <span className="ml-1">
              {isAsc ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </span>
          )}
        </div>
      );
    };
  };

  const [columnDefs, setColumnDefs] = useState([]);
  useEffect(() => {
    const createCols = () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 50,
        suppressMenu: true,
      },
      {
        headerName: "Group Code",
        field: "GroupCode",
        flex: 1,
        headerComponent: createHeaderComponent("GroupCode", "Group Code"),
        suppressMenu: true,
      },
      {
        headerName: "Group Name",
        field: "GroupName",
        flex: 2,
        headerComponent: createHeaderComponent("GroupName", "Group Name"),
        suppressMenu: true,
      },
      {
        headerName: "Action",
        field: "action",
        headerClass: "action-header",
        cellRenderer: (params) => (
          <div className="flex space-x-2 mt-1">
            <button
              className="edit-button"
              onClick={() => { setIsUpdateMode(true); console.log(params.data); navigate(`/group-master/edit/${params.data.GroupId}`) }}
              title="Edit"
              onMouseDown={(e) => e.preventDefault()}

            >
              <SquarePen color="#787474" size={18} className="mt-1" />
            </button>
            <button
              className="delete-button"
              onClick={(e) => {
                e.stopPropagation();
                onOpenSingleDelete(params.data.id, params.data.GroupName);
              }}
              title="Delete"
              onMouseDown={(e) => e.preventDefault()}
            >
              <Trash2 color="#787474" size={18} className="mt-1" />
            </button>
          </div>
        ),
        sortable: false,
      },
    ];
    setColumnDefs(createCols());
  }, [sortField, sortDirection]);

  const onSelectionChanged = () => {
    if (isOpen || !gridRef.current?.api) return;
    const selectedNodes = gridRef.current.api.getSelectedNodes() || [];
    const totalRows = gridRef.current.api.getDisplayedRowCount() || 0;

    selectedIdsRef.current = selectedNodes.map((node) => node.data.id);
    setSelectedNames(selectedNodes.map((node) => node.data.GroupName));

    const allSel = selectedNodes.length === totalRows && totalRows > 0;
    setAllSelected(allSel);
    setDeleteMode(selectedNodes.length > 1 || allSel ? "multi" : "single");
  };

  // Open delete modal for a single row via action button
  const onOpenSingleDelete = (id, name) => {
    selectedIdsRef.current = [id];
    setSelectedNames([name]);
    setDeleteId(id);
    setDeleteMode("single");
    setIsOpen(true);
  };

  const handleDeleteClick = () => {
    const count = selectedIdsRef.current.length;
    if (count === 0) {
      toast.warning("Please select at least one group to delete");
      return;
    }
    const mode = allSelected || count > 1 ? "multi" : "single";
    setDeleteMode(mode);
    setDeleteId(mode === "single" ? selectedIdsRef.current[0] : null);
    setIsOpen(true);
  };

  const onClose = () => {
    setIsOpen(false);
    setAllSelected(false);
    setPreserveSelection(false);
  };

  const confirmDelete = async () => {
    // try {
    //   if (deleteMode === "single" && deleteId !== null) {
    //     const resp = await deleteGroup(deleteId).unwrap();
    //     if (resp.success) {
    //       toast.success(resp.message || "Group deleted successfully");
    //     } else {
    //       toast.error(resp.message || "Failed to delete group");
    //     }
    //   } else {
    //     // multi delete
    //     let idsToDelete = [];
    //     if (allSelected) {
    //       // delete all on current page or maybe all returned rows
    //       idsToDelete = rowData.map((r) => r.id);
    //     } else {
    //       idsToDelete = selectedIdsRef.current;
    //     }
    //     const resp = await bulkDeleteGroup({ ids: idsToDelete }).unwrap();
    //     if (resp.success) {
    //       toast.success(resp.message || `${idsToDelete.length} groups deleted successfully`);
    //     } else {
    //       toast.error(resp.message || "Failed to delete groups");
    //     }
    //   }
    //   // after deletion refetch data
    //   refetch();
    // } catch (err) {
    //   toast.error(err?.data?.message || "Something went wrong");
    // } finally {
    //   onClose();
    // }
  };

  const handleItemsPerPageChange = (e) => {
    const newSize = parseInt(e.target.value, 10);
    setItemsPerPage(newSize);
    setCurrentPage(1);
  };

  // Adjust current page if out of bounds
  useEffect(() => {
    const total = groupsData?.totalCount || 0;
    const totalPages = Math.ceil(total / itemsPerPage);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [groupsData?.totalCount, currentPage, itemsPerPage]);

  // Preserve selection when data / page changes
  useEffect(() => {
    if (preserveSelection && selectedIdsRef.current.length > 0 && gridRef.current?.api) {
      gridRef.current.api.forEachNode((node) => {
        if (selectedIdsRef.current.includes(node.data.id)) {
          node.setSelected(true);
        }
      });
    }
  }, [rowData, currentPage, preserveSelection]);

  return (
    <div className="container p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-brandRed">Group Master List</h2>
        <div className="flex gap-2">
          <button
            className="px-4 py-2 addListTable-button"
            onClick={() => navigate("/group-master/add")}
          >
            Add
          </button>
          {selectedNames.length > 0 && (
            <button
              className="px-4 py-2 addListTable-button"
              onClick={handleDeleteClick}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      <ListView
        filterColumn={filterColumn}
        setFilterColumn={setFilterColumn}
        filterValue={filterValueInput}
        setFilterValue={setFilterValueInput}
        columnOptions={columnOptions}
        handleFilter={handleFilter}
        clearFilters={clearFilters}
        isFilterApplied={isFilterApplied}
        gridRef={gridRef}
        columnDefs={columnDefs}
        rowData={rowData}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        onSelectionChanged={onSelectionChanged}
        defaultColDef={{ sortable: true, resizable: true, suppressMenu: true }}
        loading={isLoading}
        handleItemsPerPageChange={handleItemsPerPageChange}
        totalCount={groupsData?.totalCount || 0}
      />

      <ConfirmModal
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={confirmDelete}
        title="Confirm Deletion"
        message={
          allSelected
            ? `Are you sure you want to delete all ${rowData.length} groups on this page?`
            : deleteMode === "multi"
              ? `Are you sure you want to delete ${selectedIdsRef.current.length} selected groups?`
              : `Are you sure you want to delete this group: ${selectedNames[0]}?`
        }
        selectedNames={selectedNames}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmColor="backButton"
      />

    </div>

  );
};

export default GroupMaster;
